import Commons from '../../../../../Services/Common/Common';

const Num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const Str = (v) => String(v ?? '').trim();
const FirstPipe = (v) =>
  String(v ?? '')
    .split('|')
    .find((s) => Str(s)) ?? '';
const JoinPath = (dir, file) => {
  const d = Str(dir).replace(/\\/g, '/').replace(/\/+$/, '');
  const f = Str(file).replace(/^\/+/, '');
  if (!d) return f;
  if (!f) return d;
  return `${d}/${f}`;
};

/** 조회값 후보 중 첫 비어 있지 않은 문자열 (개선 전·후 사진 경로용) */
const CoalesceStr = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
};

/**
 * TB_SF_RISK_DETAIL 조회 — 전체 경로 또는 DIR + SAVE_NAME / FILE_NAME
 * (JSON이 camelCase / PascalCase / DB 컬럼명으로 올 수 있음)
 */
const ResolveDetailRiskImagePaths = (r) => {
  let pathBefore = CoalesceStr(
    r.riskImageFilePath,
    r.RiskImageFilePath,
    r.RISK_IMAGE_FILE_PATH
  );
  if (!pathBefore) {
    pathBefore = JoinPath(
      CoalesceStr(
        r.riskImageDir,
        r.RiskImageDir,
        r.RISK_IMAGE_DIR,
        r.risk_image_dir
      ),
      CoalesceStr(
        FirstPipe(
          String(
            r.riskImageSaveName ??
              r.RiskImageSaveName ??
              r.RISK_IMAGE_SAVE_NAME ??
              ''
          )
        ),
        r.riskImageFileName,
        r.RiskImageFileName,
        r.RISK_IMAGE_FILE_NAME
      )
    );
  }

  let pathAfter = CoalesceStr(
    r.afterRiskImageFilePath,
    r.AfterRiskImageFilePath,
    r.AFTER_RISK_IMAGE_FILE_PATH
  );
  if (!pathAfter) {
    pathAfter = JoinPath(
      CoalesceStr(
        r.afterRiskImageDir,
        r.AfterRiskImageDir,
        r.AFTER_RISK_IMAGE_DIR,
        r.after_risk_image_dir
      ),
      CoalesceStr(
        FirstPipe(
          String(
            r.afterRiskImageSaveName ??
              r.AfterRiskImageSaveName ??
              r.AFTER_RISK_IMAGE_SAVE_NAME ??
              ''
          )
        ),
        r.afterRiskImageFileName,
        r.AfterRiskImageFileName,
        r.AFTER_RISK_IMAGE_FILE_NAME
      )
    );
  }

  if (!pathBefore) {
    pathBefore = CoalesceStr(
      r.riskImagePreviewFilePath,
      r.RiskImagePreviewFilePath
    );
  }
  if (!pathAfter) {
    pathAfter = CoalesceStr(
      r.afterRiskImagePreviewFilePath,
      r.AfterRiskImagePreviewFilePath
    );
  }

  return { pathBefore, pathAfter };
};

/** TB_SF_RISK_DETAIL 날짜 → 그리드 YYYY-MM-DD */
const ToDateStr = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'string') return v.length >= 10 ? v.slice(0, 10) : v;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s || null;
};

/**
 * GetRisCustom02R1 응답 파싱 (SaveRisBasicResult: data/Data, 중첩 processRiskList/Details 등)
 */
const ExtractGetRisCustom02R1FlatList = (raw) => {
  if (raw == null) return [];
  const data = raw.data ?? raw.Data;
  if (Array.isArray(data)) return data;
  const d0 = data?.[0];
  if (d0 && typeof d0 === 'object') {
    const nested =
      d0.processRiskList ?? d0.ProcessRiskList ?? d0.processList ?? [];
    if (Array.isArray(nested)) {
      const out = [];
      for (const p of nested) {
        const sid = Num(p.sriId ?? p.SriId, 0);
        const rows =
          p.details ??
          p.Details ??
          p.riskDetailList ??
          p.RiskDetailList ??
          p.Rows ??
          p.rows ??
          [];
        if (!Array.isArray(rows)) continue;
        for (const row of rows)
          out.push({
            gongJongNm: Str(p.gongJongNm ?? p.GongJongNm),
            ...row,
            sriId: Num(row.sriId ?? row.SriId, sid),
          });
      }
      return out;
    }
  }
  return [];
};

/** 번호 목록 문자열("1. ...\n2. ...")을 팝업 선택 옵션 배열로 복원 */
const ParseNumberedMultilineOptions = (raw) => {
  const t = Str(raw).trim();
  if (!t) return [];

  const fromLines = t
    .split(/\r\n|\n|\r/g)
    .map((line) =>
      line
        .split(/(?=\d+\.\s*)/g)
        .map((x) => x.trim())
        .filter((x) => x.length > 0)
    )
    .flat()
    .map((x) => x.replace(/^(\d+\.\s*)+/, '').trim())
    .filter((x) => x.length > 0);

  if (fromLines.length > 0) return fromLines;

  const fromInline = t
    .split(/(?=\d+\.\s*)/g)
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .map((x) => x.replace(/^(\d+\.\s*)+/, '').trim())
    .filter((x) => x.length > 0);

  return fromInline.length > 0 ? fromInline : [t];
};

/**
 * GetRisCustom02R1Item(GetRisCustom02R1 쿼리 컬럼) → 세부 그리드 행
 */
const MapGetRisCustom02R1ItemToDetail = (r, idx, parentSriId) => {
  const rsdStr = Str(r.rsdCd ?? r.RsdCd);
  const sriId = Num(r.sriId ?? r.SriId, parentSriId);
  const clientKey =
    rsdStr !== '' ? `rsd-${sriId}-${rsdStr}` : `row-${sriId}-${idx}`;
  const rowOrder = idx + 1;
  const rrdFromRsd = /^\d+$/.test(rsdStr) ? Num(rsdStr, 0) : 0;

  const { pathBefore, pathAfter } = ResolveDetailRiskImagePaths(r);
  const todaySafety = Str(r.todaySafety ?? r.TodaySafety);
  const reductionMeasures = Str(r.reductionMeasures ?? r.ReductionMeasures);

  return {
    clientKey,
    no: rowOrder,
    rsdCd: rsdStr !== '' ? rsdStr : clientKey,
    rrdId: rrdFromRsd,
    rowNo: rowOrder,
    categoryComment: Str(r.categoryComment ?? r.CategoryComment),
    todaySafety,
    reductionMeasures,
    reductionMeasuresOptions: ParseNumberedMultilineOptions(reductionMeasures),
    riskLevelJudgment: Str(r.riskLevelJudgment ?? r.RiskLevelJudgment),
    riskImageFilePath: Str(pathBefore),
    afterRiskImageFilePath: Str(pathAfter),
    gongJongNm: Str(r.gongJongNm),
    gaesunExDate: ToDateStr(r.gaesunExDate ?? r.GaesunExDate),
    gaesunComDate: ToDateStr(r.gaesunComDate ?? r.GaesunComDate),
    manager: '',
    riskAfterLevelJudgment: Str(
      r.riskAfterLevelJudgment ?? r.RiskAfterLevelJudgment
    ),
    condolenceNo: Str(r.condolenceNo ?? r.CondolenceNo),
    relatedEvidence: Str(r.relatedEvidence ?? r.RelatedEvidence),
    riskId: Str(r.riskId ?? r.RiskId),
    rsCd: Num(r.rsCd ?? r.RsCd, 0),
    sriId,
    todayPossibility: Str(r.todayPossibility ?? r.TodayPossibility),
    todayImportance: Str(r.todayImportance ?? r.TodayImportance),
    todayCurrentRisk: Str(r.todayCurrentRisk ?? r.TodayCurrentRisk),
    afterPossibility: Str(r.afterPossibility ?? r.AfterPossibility),
    afterImportance: Str(r.afterImportance ?? r.AfterImportance),
    afterCurrentRisk: Str(r.afterCurrentRisk ?? r.AfterCurrentRisk),
    riskImageFileName: Str(r.riskImageFileName ?? r.RiskImageFileName),
    riskImageSaveName: Str(r.riskImageSaveName ?? r.RiskImageSaveName),
    afterRiskImageFileName: Str(
      r.afterRiskImageFileName ?? r.AfterRiskImageFileName
    ),
    afterRiskImageSaveName: Str(
      r.afterRiskImageSaveName ?? r.AfterRiskImageSaveName
    ),
    ...(Str(r.workerOpinionRowYn ?? r.WorkerOpinionRowYn).trim().toUpperCase() ===
    'Y'
      ? { workerOpinionRowYn: 'Y' }
      : {}),
  };
};

/**
 * GetRisCustom02R1 응답 → [{ sriId, processNm: '', details: [] }, ...] (세부행에 gongJongNm 포함)
 */
export const MapGetRisCustom02R1ToProcesses = (raw) => {
  const flat = ExtractGetRisCustom02R1FlatList(raw);
  const bySri = new Map();
  for (const row of flat) {
    const sid = Num(row.sriId, 0);
    if (!sid) continue;
    if (!bySri.has(sid)) bySri.set(sid, []);
    bySri.get(sid).push(row);
  }
  for (const [, arr] of bySri) {
    arr.sort((a, b) =>
      Str(a.rsdCd ?? a.RsdCd).localeCompare(Str(b.rsdCd ?? b.RsdCd), undefined, {
        numeric: true,
      })
    );
  }
  const riskList = [];
  for (const [sriId, rows] of bySri) {
    riskList.push({
      sriId,
      processNm: '',
      details: rows.map((item, idx) =>
        MapGetRisCustom02R1ItemToDetail(item, idx, sriId)
      ),
    });
  }
  riskList.sort((a, b) => a.sriId - b.sriId);
  return riskList;
};

/**
 * Step01 안전보건정보에 등록된 공정(순서·명칭)을 기준으로 하고,
 * GetRisCustom02R1에서 조회한 세부 행은 동일 SRI_ID에만 병합한다.
 * @param {Array<{ sriId?: number, processNm?: string }>} safetyProcessList — SafetyInfo `processList` 형태
 * @param {Array<{ sriId: number, processNm?: string, details: object[] }>} risk02Processes — MapGetRisCustom02R1ToProcesses 결과
 */
export const MergeSafetyProcessListWithRiskDetails = (
  safetyProcessList,
  risk02Processes
) => {
  const list = Array.isArray(safetyProcessList) ? safetyProcessList : [];
  const riskList = Array.isArray(risk02Processes) ? risk02Processes : [];
  const bySri = new Map();
  for (const p of riskList) {
    const sid = Num(p.sriId, 0);
    if (!sid) continue;
    const d = p.details;
    bySri.set(sid, Array.isArray(d) ? d : []);
  }
  return list.map((sp, i) => {
    const sid = Num(sp.sriId, 0);
    return {
      sriId: sid,
      depNm: Str(sp.depNm),
      depCd: Str(sp.depCd),
      gongJongCd: Str(sp.processId),
      processNm: Str(sp.processNm) || `공정 ${i + 1}`,
      details: bySri.get(sid) ?? [],
    };
  });
};

/**
 * 불러온 실시 세부를 신규 저장용으로 복제(원본 RSD/SRI 제거).
 */
export const StripCopiedRiskDetailRowsForDraft = (details) => {
  const arr = Array.isArray(details) ? details : [];
  return arr.map((d, idx) => {
    const { workerOpinionRowYn: _omitWo, ...dRest } = d ?? {};
    const clientKey = `new-load-${Date.now()}-${idx}-${Math.random()
      .toString(36)
      .slice(2, 9)}`;
    return {
      ...dRest,
      clientKey,
      no: idx + 1,
      rowNo: idx + 1,
      rsdCd: clientKey,
      rrdId: 0,
      sriId: 0,
      newYn: 'Y',
      editYn: undefined,
      rsCd: 0,
    };
  });
};

/**
 * 기존 평가 불러오기 오버레이(by gongJongCd)를 현재 공정 목록에 합친다.
 */
export const ApplyRiskDetailOverlayToProcesses = (
  processes,
  overlay,
  assessmentRsCd
) => {
  const list = Array.isArray(processes) ? processes : [];
  if (
    !overlay ||
    typeof overlay !== 'object' ||
    overlay.byGongJongCd == null ||
    Number(overlay.rsCd) !== Number(assessmentRsCd)
  ) {
    return list;
  }
  const by = overlay.byGongJongCd;
  return list.map((p) => {
    const key = Str(p.gongJongCd);
    const tmpl = key ? by[key] : null;
    if (!Array.isArray(tmpl) || tmpl.length === 0) return p;
    const sid = Num(p.sriId, 0);
    const details = tmpl.map((d, idx) => ({
      ...d,
      sriId: sid,
      no: idx + 1,
      rowNo: idx + 1,
    }));
    return { ...p, details };
  });
};

/**
 * 근로자 의견(RIN 그리드 행) → 위험성평가 세부항목 필드 매핑
 * 유해위험요인(content) → 위험발생 상황 및 결과
 * 현재의 안전보건조치(todaySafety) → 현재의 안전보건조치
 * 위험성 감소대책(reductionMeasures) → 위험성 감소대책
 */
export const MapWorkerOpinionRowsToRiskDetailFields = (rows) =>
  (Array.isArray(rows) ? rows : []).map((r) => ({
    // 근로자청취(RIN) 이미지: pre/post 경로 + saveName(파이프 구분 가능) 중 첫 번째를 사용
    riskImageFilePath: JoinPath(
      r?.preFilePath ?? r?.PreFilePath ?? '',
      FirstPipe(r?.preSaveName ?? r?.PreSaveName ?? '')
    ),
    afterRiskImageFilePath: JoinPath(
      r?.postFilePath ?? r?.PostFilePath ?? '',
      FirstPipe(r?.postSaveName ?? r?.PostSaveName ?? '')
    ),
    riskImageFileName: FirstPipe(r?.preFileNames ?? r?.PreFileNames ?? ''),
    riskImageSaveName: FirstPipe(r?.preSaveName ?? r?.PreSaveName ?? ''),
    afterRiskImageFileName: FirstPipe(
      r?.postFileNames ?? r?.PostFileNames ?? ''
    ),
    afterRiskImageSaveName: FirstPipe(r?.postSaveName ?? r?.PostSaveName ?? ''),
    riskImagePreviewFilePath: '',
    afterRiskImagePreviewFilePath: '',
    categoryComment: Str(r?.content ?? r?.Content ?? ''),
    todaySafety: Str(r?.todaySafety ?? r?.TodaySafety ?? ''),
    reductionMeasures: Str(r?.reductionMeasures ?? r?.ReductionMeasures ?? ''),
  }));

export const CreateEmptyDetailRow = (sriId) => {
  const clientKey = `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    clientKey,
    rsdCd: clientKey,
    rrdId: 0,
    rowNo: 0,
    newYn: 'Y',
    categoryComment: '',
    todaySafety: '',
    reductionMeasures: '',
    reductionMeasuresOptions: [],
    riskLevelJudgment: '',
    riskImageFilePath: '',
    afterRiskImageFilePath: '',
    gaesunExDate: null,
    gaesunComDate: null,
    manager: '',
    riskAfterLevelJudgment: '',
    condolenceNo: '',
    relatedEvidence: '',
    riskImageFileName: '',
    riskImageSaveName: '',
    riskImagePreviewFilePath: '',
    afterRiskImageFileName: '',
    afterRiskImageSaveName: '',
    afterRiskImagePreviewFilePath: '',
    sriId,
  };
};

/** 그리드 gaesunExDate/gaesunComDate → 서버 DateTime? */
const ToSaveDateTime = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  const s = String(v).trim().slice(0, 10);
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** INSERT 시 RsdCd 비움(신규 행은 new-/row- 접두·빈값) */
const ResolveSaveRsdCd = (r) => {
  const s = Str(r.rsdCd);
  if (!s || s.startsWith('new-') || s.startsWith('row-')) return null;
  return s;
};

/**
 * SaveRisCustom02U1Item — TB_SF_RISK_DETAIL
 * GongJongNm: 활동 로그용(공정명)
 */
const MapDetailToSaveItem = (r, gongJongNm) => ({
  RsdCd: ResolveSaveRsdCd(r),
  SriId: Num(r.sriId, 0),
  GongJongNm: Str(gongJongNm ?? r.gongJongNm ?? ''),
  CategoryComment: Str(r.categoryComment),
  TodaySafety: Str(r.todaySafety),
  ReductionMeasures: Str(r.reductionMeasures),
  RiskLevelJudgment: Str(r.riskLevelJudgment),
  GaesunExDate: ToSaveDateTime(r.gaesunExDate),
  GaesunComDate: ToSaveDateTime(r.gaesunComDate),
  RiskId: Str(r.riskId ?? ''),
  RiskAfterLevelJudgment: Str(r.riskAfterLevelJudgment),
  CondolenceNo: Str(r.condolenceNo),
  RelatedEvidence: Str(r.relatedEvidence),
  TodayPossibility: Str(r.todayPossibility ?? ''),
  TodayImportance: Str(r.todayImportance ?? ''),
  TodayCurrentRisk: Str(r.todayCurrentRisk ?? ''),
  AfterPossibility: Str(r.afterPossibility ?? ''),
  AfterImportance: Str(r.afterImportance ?? ''),
  AfterCurrentRisk: Str(r.afterCurrentRisk ?? ''),
  RiskImageFileName: Str(r.riskImageFileName ?? ''),
  RiskImageSaveName: Str(r.riskImageSaveName ?? ''),
  RiskImageFilePath: Str(r.riskImageFilePath ?? ''),
  AfterRiskImageFileName: Str(r.afterRiskImageFileName ?? ''),
  AfterRiskImageSaveName: Str(r.afterRiskImageSaveName ?? ''),
  AfterRiskImageFilePath: Str(r.afterRiskImageFilePath ?? ''),
  WorkerOpinionRowYn:
    Str(r.workerOpinionRowYn ?? r.WorkerOpinionRowYn)
      .trim()
      .toUpperCase() === 'Y'
      ? 'Y'
      : 'N',
});

/** GetRisCustom01R1 등 안전보건 raw — 평가방식(rsMethod) 추출 */
export const ExtractRsMethodFromSafety01Raw = (raw) => {
  try {
    if (!raw || typeof raw !== 'object') return '';
    const items = raw.Data ?? raw.data;
    const firstRawRow = Array.isArray(items) ? items[0] : items;
    if (!firstRawRow || typeof firstRawRow !== 'object') return '';
    const riskMasterList =
      firstRawRow.riskMasterList ?? firstRawRow.RiskMasterList ?? [];
    const firstRiskMaster = Array.isArray(riskMasterList)
      ? riskMasterList[0]
      : riskMasterList;
    const method =
      firstRiskMaster?.rsMethod ??
      firstRiskMaster?.RsMethod ??
      firstRawRow?.rsMethod ??
      firstRawRow?.RsMethod ??
      '';
    return String(method ?? '').trim();
  } catch {
    return '';
  }
};

/**
 * GetRisCustomR1 응답 — 지정 RsCd에 해당하는 RiskMaster의 MatrixList(평가척도)
 */
export const ExtractRiskMatrixListFromRisCustomR1Raw = (raw, rsCd) => {
  try {
    if (!raw || typeof raw !== 'object') return [];
    const want = Number(rsCd);
    if (!Number.isFinite(want) || want <= 0) return [];
    const first = raw?.Data?.[0] ?? raw?.data?.[0] ?? {};
    const list =
      first?.RiskMasterList ??
      first?.riskMasterList ??
      raw?.RiskMasterList ??
      raw?.riskMasterList ??
      [];
    if (!Array.isArray(list)) return [];
    const target = list.find(
      (item) => Number(item?.RsCd ?? item?.rsCd ?? 0) === want
    );
    if (!target || typeof target !== 'object') return [];
    const matrix = target.matrixList ?? target.MatrixList ?? [];
    return Array.isArray(matrix) ? matrix : [];
  } catch {
    return [];
  }
};

const MatrixRowSortKey = (m) => Number(m.SortOrdr ?? m.sortOrdr ?? 0);

/**
 * MatrixList 정렬·정규화 (SortOrdr → MinScore)
 */
export const NormalizeRiskMatrixRowsForLookup = (matrixList) =>
  [...(matrixList || [])]
    .map((m) => ({
      levelNm: String(m.LevelNm ?? m.levelNm ?? '').trim(),
      minScore: Number(m.MinScore ?? m.minScore ?? NaN),
      maxScore: Number(m.MaxScore ?? m.maxScore ?? NaN),
      sortOrdr: MatrixRowSortKey(m),
    }))
    .filter(
      (r) =>
        r.levelNm &&
        (Number.isFinite(r.minScore) || Number.isFinite(r.maxScore))
    )
    .sort((a, b) => a.sortOrdr - b.sortOrdr || a.minScore - b.minScore);

/**
 * 빈도·강도 점수(가능성×중대성)가 MinScore~MaxScore 구간에 들어가면 해당 LevelNm 반환
 */
export const ResolveLevelNmFromRiskMatrix = (score, matrixList) => {
  const n = Number(String(score ?? '').trim());
  if (!Number.isFinite(n)) return '';
  const rows = NormalizeRiskMatrixRowsForLookup(matrixList);
  if (!rows.length) return '';
  for (const r of rows) {
    const lo = Number.isFinite(r.minScore) ? r.minScore : -Infinity;
    const hi = Number.isFinite(r.maxScore) ? r.maxScore : Infinity;
    if (n >= lo && n <= hi) return r.levelNm;
  }
  return '';
};

/** 평가방식 문자열 비교용(빈도·강도법 판별) */
export const NormalizeRsMethodKey = (v) =>
  String(v ?? '')
    .replace(/[·ㆍ]/g, '')
    .replace(/\s+/g, '')
    .trim();

export const IsFrequencyIntensityRsMethod = (v) =>
  NormalizeRsMethodKey(v) === NormalizeRsMethodKey('빈도·강도법');

/** 빈도·강도 — 가능성×중대성 점수 문자열 */
export const CalcFiRiskScoreString = (possibility, importance) => {
  const p = Number(String(possibility ?? '').trim());
  const i = Number(String(importance ?? '').trim());
  if (!Number.isFinite(p) || !Number.isFinite(i)) return '';
  return String(p * i);
};

/**
 * 빈도·강도 행 — 점수 및 matrixList 구간에 맞는 LevelNm을 risk 판단 필드에 반영(저장·그리드 동기화)
 */
export const BuildFiRiskMatrixDerivedPatch = (row, matrixList) => {
  const matrix = Array.isArray(matrixList) ? matrixList : [];
  const todaySc = CalcFiRiskScoreString(
    row?.todayPossibility,
    row?.todayImportance
  );
  const afterSc = CalcFiRiskScoreString(
    row?.afterPossibility,
    row?.afterImportance
  );
  return {
    todayCurrentRisk: todaySc,
    afterCurrentRisk: afterSc,
    riskLevelJudgment: ResolveLevelNmFromRiskMatrix(todaySc, matrix),
    riskAfterLevelJudgment: ResolveLevelNmFromRiskMatrix(afterSc, matrix),
  };
};

export const RiskAssessment_Schema = {
  /** useSave(validateRequiredWithMessage) — 공정 단위 저장 payload 행 */
  fields: {
    processNm: { initial: '', label: '공정명', required: 'Y' },
  },
  mapper: {
    /** GetRisCustom02R1 */
    search1: {
      RsCd: (_, context) => {
        const n = Num(context.rsCd, 0);
        return Number.isFinite(n) && n > 0 ? n : 0;
      },
      SearchText: (_, context) =>
        Commons.EncryptAES(String(context.searchText ?? '').trim()),
    },
    /**
     * SaveRisCustom02U1 — SaveRisCustom02U1Request
     * row: { rsCd, userId, stepSeq, processNm, items: 세부행[] }
     * UserId: 서버에서 DecryptAES 처리 → 클라이언트는 EncryptAES
     */
    save1: {
      RsCd: (row) => Num(row.rsCd, 0),
      UserId: (_, context) => context.userInfo?.userid || '',
      StepSeq: (row) => Num(row.stepSeq, 0),
      Items: (row) => {
        const items = Array.isArray(row.items) ? row.items : [];
        const gnm = Str(row.processNm ?? '');
        return items.map((item) => MapDetailToSaveItem(item, gnm));
      },
    },
    /** DeleteRisCustom02D1 */
    delete1: {
      // TB_SF_RISK_DETAIL 삭제 — DeleteRisCustom02D1(List<DeleteRisCustom02D1Info>)
      // DeleteRisCustom02D1Info { RsdCd }
      RsdCd: (row) => Str(row.rsdCd),
    },
    /**
     * 위험성평가 실시 안 표준모델요인 조회 — GetRisBasic01R6 (RIS_BASIC_01과 동일)
     */
    search7: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || ''),
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || ''),
    },
  },
};
